import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../auth/userContext";
import { BudgetGroup, BudgetGroupInvite } from "../types/budgethelper";
import "../css/styles.css";
import { useGetInvites } from "./hooks/useGetInvites";
import { useDeleteInvite } from "./hooks/useDeleteInvite";
import { useCreateInvite } from "./hooks/useCreateInvite";
import { useUpdateBudgetGroup } from "./hooks/useUpdateBudgetGroup";
import { useCreateBudgetGroup } from "./hooks/useCreateBudgetGroup";
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import MailIcon from '@mui/icons-material/Mail';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';


export const BudgetGroups = () => {
  const { userProfile, budgetGroups, loading } = useUser();
  const { invites } = useGetInvites();
  const { createInvite } = useCreateInvite();
  const { deleteInvite } = useDeleteInvite();
  const { createBudgetGroup } = useCreateBudgetGroup();
  const { joinBudgetGroup, leaveBudgetGroup, updateBudgetGroupName } = useUpdateBudgetGroup();
  const navigate = useNavigate();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState<BudgetGroup | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<BudgetGroup | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [editGroupName, setEditGroupName] = useState("");
  const [activeTab, setActiveTab] = useState<"groups" | "invites">("groups");

  if (!userProfile || loading) return <p>Please Wait I'm Loading...</p>;

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile || !newGroupName.trim()) return;

    if (budgetGroups.length >= 5) {
      alert("Maximum 5 budget groups allowed");
      return;
    }

    await createBudgetGroup(newGroupName, userProfile);
    setNewGroupName("");
    setShowCreateModal(false);
  };

  const handleAcceptInvite = async (invite: BudgetGroupInvite) => {
    await joinBudgetGroup(invite);
    await deleteInvite(invite.id);
  };

  const handleDeclineInvite = async (inviteId: string) => {
    await deleteInvite(inviteId);
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup || !inviteEmail.trim()) return;

    // TODO - show error if this fails
    await createInvite({
      currentUser: userProfile,
      invitedEmail: inviteEmail,
      budgetGroup: selectedGroup
    });
    setShowInviteModal(false);
    setInviteEmail("");
    alert("Invite sent successfully!");
  };

  const handleEditGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup || !editGroupName.trim()) return;

    await updateBudgetGroupName(selectedGroup.id, editGroupName);
    setShowEditModal(false);
    setSelectedGroup(null);
    setEditGroupName("");
  };

  const handleLeaveGroup = async (group: BudgetGroup) => {
    if (!userProfile) return;
    setLeaveConfirm(group);
  };

  const confirmLeaveGroup = async () => {
    if (!userProfile || !leaveConfirm) return;
    await leaveBudgetGroup(leaveConfirm.id, userProfile.userId);
    setLeaveConfirm(null);
  };

  const openInviteModal = (group: BudgetGroup) => {
    setSelectedGroup(group);
    setShowInviteModal(true);
  };

  const openEditModal = (group: BudgetGroup) => {
    setSelectedGroup(group);
    setEditGroupName(group.friendlyName);
    setShowEditModal(true);
  };

  if (!userProfile || loading) return <p>Loading...</p>;

  return (
    <div className="expense-tracker-modern">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div>
            <h1 className="app-title">Budget Groups</h1>
            <p className="total-label">{budgetGroups.length}/5 Groups</p>
          </div>
          <div className="header-actions">
            <button className="icon-btn" onClick={() => navigate("/expense-tracker")}>
              <ArrowBackIcon /> Back
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="filter-chips">
        <button
          className={`chip ${activeTab === "groups" ? "active" : ""}`}
          onClick={() => setActiveTab("groups")}
        >
          My Groups ({budgetGroups.length})
        </button>
        <button
          className={`chip ${activeTab === "invites" ? "active" : ""}`}
          onClick={() => setActiveTab("invites")}
        >
          Invites ({invites.length})
        </button>
      </div>

      {/* Groups Tab */}
      {activeTab === "groups" && (
        <div className="transactions-list">
          {budgetGroups.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><GroupIcon style={{ fontSize: 64 }} /></div>
              <p>No budget groups yet</p>
              <p className="empty-subtitle">Create one to get started</p>
            </div>
          ) : (
            budgetGroups.map((group) => (
              <div key={group.id} className="budget-group-card">
                <div className="budget-group-icon">
                  <GroupIcon style={{ fontSize: 28 }} />
                </div>
                <div className="budget-group-info">
                  <h3 className="budget-group-name">{group.friendlyName}</h3>
                  <p className="budget-group-members">{group.userIds?.length || 0} {(group.userIds?.length || 0) === 1 ? 'member' : 'members'}</p>
                </div>
                <div className="budget-group-actions">
                  <button
                    className="action-btn-compact invite-btn"
                    onClick={() => openInviteModal(group)}
                    title="Invite"
                  >
                    <PersonAddIcon fontSize="small" />
                  </button>
                  <button
                    className="action-btn-compact edit-btn"
                    onClick={() => openEditModal(group)}
                    title="Edit"
                  >
                    <EditIcon fontSize="small" />
                  </button>
                  <button
                    className="action-btn-compact leave-btn"
                    onClick={() => handleLeaveGroup(group)}
                    title="Leave"
                  >
                    <ExitToAppIcon fontSize="small" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Invites Tab */}
      {activeTab === "invites" && (
        <div className="transactions-list">
          {invites.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><MailIcon style={{ fontSize: 64 }} /></div>
              <p>No pending invites</p>
            </div>
          ) : (
            invites.map((invite) => (
              <div key={invite.id} className="transaction-card">
                <div className="transaction-content">
                  <div className="transaction-main">
                    <div className="transaction-left">
                      <div className="category-icon invite"><MailIcon /></div>
                      <div>
                        <span className="shop-name">{invite.groupName}</span>
                        <div className="source-text">
                          Invited by {invite.invitedByName}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="transaction-footer">
                    <button
                      className="btn-primary"
                      onClick={() => handleAcceptInvite(invite)}
                    >
                      <CheckIcon style={{ fontSize: 18 }} /> Accept
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleDeclineInvite(invite.id)}
                    >
                      <CloseIcon style={{ fontSize: 18 }} /> Decline
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* FAB - Only show if less than 5 groups */}
      {budgetGroups.length < 5 && (
        <button className="fab" onClick={() => setShowCreateModal(true)}>
          <span className="fab-icon">+</span>
          <span className="fab-text">Create</span>
        </button>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create Budget Group</h2>
            <form onSubmit={handleCreateGroup}>
              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  placeholder="Enter group name"
                  value={newGroupName}
                  required
                  onChange={(e) => setNewGroupName(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Invite to {selectedGroup.friendlyName}</h2>
            <form onSubmit={handleSendInvite}>
              <div className="form-group">
                <label>User Email</label>
                <input
                  type="email"
                  placeholder="Enter user email"
                  value={inviteEmail}
                  required
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Group</h2>
            <form onSubmit={handleEditGroup}>
              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  placeholder="Enter group name"
                  value={editGroupName}
                  required
                  onChange={(e) => setEditGroupName(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Leave Group Confirmation Dialog */}
      {leaveConfirm && (
        <div className="modal-overlay" onClick={() => setLeaveConfirm(null)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon"><ExitToAppIcon style={{ fontSize: 48 }} /></div>
            <h2>Leave Budget Group?</h2>
            <p>Are you sure you want to leave "{leaveConfirm.friendlyName}"?</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setLeaveConfirm(null)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={confirmLeaveGroup}>
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
